import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { payrollAPI } from '../../services/api';
import { useToast } from '../Toast';
import { XMarkIcon, CurrencyRupeeIcon, BanknotesIcon } from '@heroicons/react/24/outline';

const PayrollPayModal = ({ payroll, onClose, onSuccess }) => {
  const { showToast } = useToast();
  const [paymentMethod, setPaymentMethod] = useState('bank_transfer');
  const [transactionReference, setTransactionReference] = useState('');
  const [remarks, setRemarks] = useState('');

  const payMutation = useMutation({
    mutationFn: (data) => payrollAPI.markAsPaid(payroll.payroll_id, data),
    onSuccess: () => {
      showToast('Payroll marked as paid successfully', 'success');
      onSuccess();
    },
    onError: (error) => {
      showToast(error.response?.data?.detail || 'Failed to mark payroll as paid', 'error');
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    payMutation.mutate({
      payment_method: paymentMethod,
      transaction_reference: transactionReference || null,
      remarks: remarks || null,
    });
  };

  const getMonthName = (month) => {
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    return months[month - 1];
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const paymentMethods = [
    { value: 'bank_transfer', label: 'Bank Transfer' },
    { value: 'cash', label: 'Cash' },
    { value: 'cheque', label: 'Cheque' },
  ];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-gray-800 rounded-xl shadow-2xl w-full max-w-md mx-4 border border-gray-700">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-700">
          <div>
            <h2 className="text-xl font-semibold text-white">Mark as Paid</h2>
            <p className="text-sm text-gray-400 mt-1">
              {getMonthName(payroll.month)} {payroll.year}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Payment Summary */}
          <div className="bg-green-900/30 border border-green-500/50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <CurrencyRupeeIcon className="w-8 h-8 text-green-400" />
                <div>
                  <p className="text-sm text-green-300">Net Payable Amount</p>
                  <p className="text-2xl font-bold text-green-400">
                    {formatCurrency(payroll.net_salary)}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Employee Info */}
          <div className="bg-gray-700/50 rounded-lg p-4">
            <p className="text-sm text-gray-400">Employee</p>
            <p className="text-white font-medium">{payroll.employee_name}</p>
            <p className="text-sm text-gray-400 mt-1">{payroll.employee_id}</p>
          </div>

          {/* Payment Method */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Payment Method <span className="text-red-400">*</span>
            </label>
            <div className="grid grid-cols-3 gap-2">
              {paymentMethods.map((method) => (
                <button
                  key={method.value}
                  type="button"
                  onClick={() => setPaymentMethod(method.value)}
                  className={`px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
                    paymentMethod === method.value
                      ? 'bg-blue-600 border-blue-500 text-white'
                      : 'bg-gray-700 border-gray-600 text-gray-300 hover:border-gray-500'
                  }`}
                >
                  {method.label}
                </button>
              ))}
            </div>
          </div>

          {/* Transaction Reference */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Transaction Reference
            </label>
            <input
              type="text"
              value={transactionReference}
              onChange={(e) => setTransactionReference(e.target.value)}
              placeholder="e.g., UTR number, Cheque number"
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white placeholder-gray-400"
            />
          </div>

          {/* Remarks */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Remarks
            </label>
            <textarea
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              placeholder="Optional notes..."
              rows={2}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white placeholder-gray-400 resize-none"
            />
          </div>

          {/* Buttons */}
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-300 hover:text-white bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={payMutation.isPending}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              <BanknotesIcon className="w-4 h-4" />
              <span>{payMutation.isPending ? 'Processing...' : 'Confirm Payment'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PayrollPayModal;
